import React from 'react';

// Custom pagination component for react-data-table-component
// Defensive: works with common prop names the library provides.
export default function DatatablePagination(props) {
  const {
    rowsPerPage = 10,
    rowCount = 0,
    currentPage = 1,
    onChangePage,
    onChangeRowsPerPage,
    rowsPerPageOptions = [10, 20, 30, 50],
  } = props;

  const totalRows = Number(rowCount) || 0;
  const perPage = Number(rowsPerPage) || 10;
  const totalPages = Math.max(1, Math.ceil(totalRows / perPage));
  const page = Number(currentPage) || 1;

  const handlePrev = () => {
    if (page <= 1) return;
    try {
      onChangePage(page - 1);
    } catch (e) {
      // fallback: some implementations expect 1-based page
      try { onChangePage(page - 1); } catch (e2) {}
    }
  };

  const handleNext = () => {
    if (page >= totalPages) return;
    try {
      onChangePage(page + 1);
    } catch (e) {
      try { onChangePage(page + 1); } catch (e2) {}
    }
  };

  const handlePage = (p) => {
    if (p < 1 || p > totalPages) return;
    try { onChangePage(p); } catch (e) { try { onChangePage(p); } catch (ee) {} }
  };

  const handlePerPageChange = (e) => {
    const newPer = Number(e.target.value);
    if (!newPer) return;
    try {
      // Some implementations accept (newPer, page)
      onChangeRowsPerPage(newPer, 1);
    } catch (err) {
      try { onChangeRowsPerPage(newPer); } catch (err2) {}
    }
  };

  // Build a short pagination range (show up to 5 pages)
  const getPageRange = () => {
    const maxButtons = 5;
    let start = Math.max(1, page - Math.floor(maxButtons / 2));
    let end = start + maxButtons - 1;
    if (end > totalPages) {
      end = totalPages;
      start = Math.max(1, end - maxButtons + 1);
    }
    const range = [];
    for (let i = start; i <= end; i++) range.push(i);
    return range;
  };

  const pageRange = getPageRange();

  const start = totalRows === 0 ? 0 : (page - 1) * perPage + 1;
  const end = Math.min(page * perPage, totalRows);

  return (
    <div className="data-table-pagination flex items-center justify-center gap-3 px-4 py-3">
      <div className="flex items-center gap-2">
        <label className="text-sm text-gray-600 dark:text-gray-300">Rows</label>
        <div className="relative">
          <select
            className="appearance-none pl-2 pr-8 py-1 text-sm rounded border border-gray-200/70 dark:border-gray-600/70 bg-surface dark:bg-gray-870 text-gray-800 dark:text-gray-200"
            value={perPage}
            onChange={handlePerPageChange}
            aria-label="Rows per page"
          >
            {rowsPerPageOptions && rowsPerPageOptions.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
          {/* custom arrow */}
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
            <svg className="h-4 w-4 text-gray-500 dark:text-gray-200" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
              <path d="M5.23 7.21a.75.75 0 011.06.02L10 11.293l3.71-4.06a.75.75 0 011.08 1.04l-4.25 4.655a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z" />
            </svg>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={handlePrev}
          disabled={page <= 1}
          className="inline-flex items-center px-2 py-1 border rounded-md text-sm bg-transparent border-gray-200/70 dark:border-gray-600/70 text-gray-700 dark:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700"
          aria-label="Previous page"
        >
          <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M12.707 14.707a1 1 0 01-1.414 0L6.586 10l4.707-4.707a1 1 0 011.414 1.414L9.414 10l3.293 3.293a1 1 0 010 1.414z" clipRule="evenodd" />
          </svg>
        </button>

        <nav className="flex items-center gap-1" aria-label="Pagination">
          {pageRange.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => handlePage(p)}
              className={`px-3 py-1 rounded-full text-sm ${p === page ? 'bg-brand-600 text-white shadow' : 'bg-surface dark:bg-gray-870 text-gray-700 dark:text-gray-200 border border-gray-200/70 dark:border-gray-600/70 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-brand-300'}`}
              aria-current={p === page ? 'page' : undefined}
            >
              {p}
            </button>
          ))}
        </nav>

        <button
          type="button"
          onClick={handleNext}
          disabled={page >= totalPages}
          className="inline-flex items-center px-2 py-1 border rounded-md text-sm bg-transparent border-gray-200/70 dark:border-gray-600/70 text-gray-700 dark:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700"
          aria-label="Next page"
        >
          <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M7.293 5.293a1 1 0 011.414 0L13.414 10l-4.707 4.707a1 1 0 01-1.414-1.414L10.586 10 7.293 6.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>
      </div>

      <div className="text-sm text-gray-600 dark:text-gray-400">
        Showing {start}-{end} of {totalRows}
      </div>
    </div>
  );
}

